import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useLanguage } from '../context/LanguageContext'
import { Button } from './ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog'
import { Textarea } from './ui/textarea'
import { toast } from 'sonner'
import axios from 'axios'
import { Lightbulb, Plus, Loader2, Mic, Square, Type } from 'lucide-react'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

// Storage key for FAB position
const FAB_POSITION_KEY = 'kaizen_fab_position'

// Get saved position or default
const getInitialPosition = () => {
  try {
    const saved = localStorage.getItem(FAB_POSITION_KEY)
    if (saved) {
      const pos = JSON.parse(saved)
      if (pos.x >= 0 && pos.y >= 0) {
        return pos
      }
    }
  } catch (e) {
    console.error('Error reading FAB position:', e)
  }
  return { x: window.innerWidth - 72, y: window.innerHeight - 140 }
}

export default function IdeaFAB() {
  const { t, locale } = useLanguage()
  const navigate = useNavigate()
  const location = useLocation()
  const [showQuickCapture, setShowQuickCapture] = useState(false)
  const [captureMode, setCaptureMode] = useState('text') // 'text' or 'audio'
  const [body, setBody] = useState('')
  const [saving, setSaving] = useState(false)
  
  // Audio recording states
  const [isRecording, setIsRecording] = useState(false)
  const [isPreparing, setIsPreparing] = useState(false) // While getting mic access
  const [recordingTime, setRecordingTime] = useState(0)
  const mediaRecorderRef = useRef(null)
  const audioChunksRef = useRef([])
  const recordingTimerRef = useRef(null)
  
  const MAX_RECORDING_SECONDS = 300 // 5 minutes
  
  // Dragging state
  const [position, setPosition] = useState(getInitialPosition)
  const [isDragging, setIsDragging] = useState(false)
  const [hasMoved, setHasMoved] = useState(false)
  const dragStartPos = useRef({ x: 0, y: 0 })
  const fabStartPos = useRef({ x: 0, y: 0 })

  // Hidden paths check
  const hiddenPaths = ['/auth', '/ideas']
  const shouldHide = hiddenPaths.some(path => location.pathname.startsWith(path))

  // Update position on window resize
  useEffect(() => {
    const handleResize = () => {
      setPosition(prev => ({
        x: Math.min(prev.x, window.innerWidth - 56),
        y: Math.min(prev.y, window.innerHeight - 56)
      }))
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current)
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop()
      }
    }
  }, [])

  // Drag handlers
  const handleDragStart = useCallback((clientX, clientY) => {
    setIsDragging(true)
    setHasMoved(false)
    dragStartPos.current = { x: clientX, y: clientY }
    fabStartPos.current = { x: position.x, y: position.y }
  }, [position.x, position.y])

  const handleDragMove = useCallback((clientX, clientY) => {
    if (!isDragging) return
    
    const deltaX = clientX - dragStartPos.current.x
    const deltaY = clientY - dragStartPos.current.y
    
    if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
      setHasMoved(true)
    }
    
    const newX = Math.max(8, Math.min(window.innerWidth - 64, fabStartPos.current.x + deltaX))
    const newY = Math.max(8, Math.min(window.innerHeight - 64, fabStartPos.current.y + deltaY))
    
    setPosition({ x: newX, y: newY })
  }, [isDragging])

  const handleDragEnd = useCallback(() => {
    if (isDragging && hasMoved) {
      try {
        localStorage.setItem(FAB_POSITION_KEY, JSON.stringify(position))
      } catch (e) {
        console.error('Error saving FAB position:', e)
      }
    }
    setIsDragging(false)
  }, [isDragging, hasMoved, position])

  // Mouse events
  const handleMouseDown = useCallback((e) => {
    e.preventDefault()
    handleDragStart(e.clientX, e.clientY)
  }, [handleDragStart])

  const handleMouseMove = useCallback((e) => {
    handleDragMove(e.clientX, e.clientY)
  }, [handleDragMove])

  const handleMouseUp = useCallback(() => {
    handleDragEnd()
  }, [handleDragEnd])

  // Touch events
  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0]
    handleDragStart(touch.clientX, touch.clientY)
  }, [handleDragStart])

  const handleTouchMove = useCallback((e) => {
    const touch = e.touches[0]
    handleDragMove(touch.clientX, touch.clientY)
  }, [handleDragMove])

  const handleTouchEnd = useCallback(() => {
    handleDragEnd()
  }, [handleDragEnd])

  // Add/remove global mouse listeners when dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
      return () => {
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  const handleClick = useCallback(() => {
    if (!hasMoved) {
      setShowQuickCapture(true)
      setCaptureMode('text')
    }
  }, [hasMoved])

  const handleQuickSave = async () => {
    if (!body.trim()) {
      toast.error(locale === 'es-MX' ? 'Escribe algo primero' : 'Write something first')
      return
    }
    
    setSaving(true)
    try {
      const response = await axios.post(`${API}/crazy-ideas`, {
        body: body.trim()
      })
      toast.success(t('ideas.ideaSaved'))
      setBody('')
      setShowQuickCapture(false)
      navigate(`/ideas/${response.data.id}`)
    } catch (error) {
      console.error('[IdeaFAB] Error saving idea:', error)
      toast.error(t('ideas.errorSaving'))
    } finally {
      setSaving(false)
    }
  }

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      handleQuickSave()
    }
  }

  // Audio Recording Functions
  const startRecording = async () => {
    // Show preparing state immediately
    setIsPreparing(true)
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      
      // Now we have mic access, start recording
      setIsPreparing(false)
      setIsRecording(true)
      setRecordingTime(0)
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      })
      
      mediaRecorderRef.current = mediaRecorder
      audioChunksRef.current = []
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { 
          type: mediaRecorder.mimeType 
        })
        stream.getTracks().forEach(track => track.stop())
        
        // Save idea with audio
        await saveIdeaWithAudio(audioBlob)
      }
      
      mediaRecorder.start(100) // Collect data every 100ms for faster response
      
      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= MAX_RECORDING_SECONDS - 1) {
            stopRecording()
            return prev
          }
          return prev + 1
        })
      }, 1000)
      
    } catch (error) {
      console.error('Error starting recording:', error)
      setIsPreparing(false)
      setIsRecording(false)
      setRecordingTime(0)
      toast.error(locale === 'es-MX' ? 'Error al acceder al micrófono. Verifica los permisos.' : 'Error accessing microphone. Check permissions.')
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      // Check if recording is too short
      if (recordingTime < 1) {
        toast.error(locale === 'es-MX' ? 'Grabación muy corta. Mantén presionado al menos 1 segundo.' : 'Recording too short. Hold for at least 1 second.')
        // Cancel the recording instead of saving
        mediaRecorderRef.current.onstop = () => {
          const stream = mediaRecorderRef.current.stream
          if (stream) {
            stream.getTracks().forEach(track => track.stop())
          }
        }
        mediaRecorderRef.current.stop()
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current)
        }
        setIsRecording(false)
        setRecordingTime(0)
        return
      }
      mediaRecorderRef.current.stop()
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current)
    }
    setIsRecording(false)
  }

  const saveIdeaWithAudio = async (audioBlob) => {
    // Validate audio blob before saving
    if (!audioBlob || audioBlob.size < 100) {
      toast.error(locale === 'es-MX' ? 'La grabación está vacía. Intenta de nuevo manteniedo presionado más tiempo.' : 'Recording is empty. Try again and hold longer.')
      setRecordingTime(0)
      return
    }
    
    setSaving(true)
    try {
      // First create the idea
      const ideaResponse = await axios.post(`${API}/crazy-ideas`, {
        body: locale === 'es-MX' ? '🎙️ Nota de voz' : '🎙️ Voice note'
      })
      
      // Then upload the audio
      const formData = new FormData()
      formData.append('audio', audioBlob, 'recording.webm')
      
      await axios.post(`${API}/crazy-ideas/${ideaResponse.data.id}/audio`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      
      toast.success(locale === 'es-MX' ? 'Idea con audio guardada' : 'Idea with audio saved')
      setShowQuickCapture(false)
      setRecordingTime(0)
      
      // Navigate to ideas list (not detail page)
      navigate('/ideas')
      
    } catch (error) {
      console.error('Error saving idea with audio:', error)
      const errorMsg = error.response?.data?.detail || (locale === 'es-MX' ? 'Error al guardar' : 'Error saving')
      toast.error(errorMsg)
    } finally {
      setSaving(false)
    }
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const handleCloseModal = () => {
    if (isRecording) {
      // Cancel recording if closing
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop()
        // Clear the onstop handler to prevent saving
        mediaRecorderRef.current.onstop = () => {
          const stream = mediaRecorderRef.current.stream
          if (stream) {
            stream.getTracks().forEach(track => track.stop())
          }
        }
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current)
      }
      setIsRecording(false)
      setRecordingTime(0)
    }
    setShowQuickCapture(false)
    setCaptureMode('text')
    setBody('')
  }

  // Don't render if on hidden path
  if (shouldHide) return null

  return (
    <>
      {/* Draggable FAB Button */}
      <button
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className={`fixed z-50 w-14 h-14 rounded-full bg-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-500/30 flex items-center justify-center transition-colors ${isDragging ? 'cursor-grabbing scale-110' : 'cursor-grab active:scale-95'}`}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          touchAction: 'none',
          transition: isDragging ? 'none' : 'transform 0.1s, box-shadow 0.2s'
        }}
        data-testid="idea-fab"
        aria-label={t('ideas.quickCapture')}
      >
        <Lightbulb className="w-6 h-6 pointer-events-none" />
      </button>

      {/* Quick Capture Modal */}
      <Dialog open={showQuickCapture} onOpenChange={handleCloseModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-amber-500" />
              {t('ideas.quickCapture')}
            </DialogTitle>
          </DialogHeader>
          
          {/* Mode Tabs */}
          <div className="flex gap-2 border-b pb-3">
            <Button
              variant={captureMode === 'text' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setCaptureMode('text')}
              className="rounded-full"
              data-testid="mode-text-btn"
            >
              <Type className="w-4 h-4 mr-2" />
              {locale === 'es-MX' ? 'Texto' : 'Text'}
            </Button>
            <Button
              variant={captureMode === 'audio' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setCaptureMode('audio')}
              className="rounded-full"
              data-testid="mode-audio-btn"
            >
              <Mic className="w-4 h-4 mr-2" />
              {locale === 'es-MX' ? 'Audio' : 'Audio'}
            </Button>
          </div>
          
          <div className="py-4">
            {captureMode === 'text' ? (
              <>
                <Textarea
                  placeholder={t('ideas.bodyPlaceholder')}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="min-h-[120px] resize-none"
                  autoFocus
                  data-testid="quick-idea-input"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  {locale === 'es-MX' 
                    ? 'Tip: Arrastra la 💡 a donde quieras • Cmd/Ctrl + Enter para guardar'
                    : 'Tip: Drag the 💡 anywhere • Cmd/Ctrl + Enter to save'}
                </p>
              </>
            ) : (
              <div className="flex flex-col items-center py-6">
                {isPreparing ? (
                  <>
                    <div className="w-24 h-24 rounded-full bg-amber-500 text-white flex items-center justify-center animate-pulse">
                      <Loader2 className="w-10 h-10 animate-spin" />
                    </div>
                    <p className="text-sm text-muted-foreground mt-4">
                      {locale === 'es-MX' 
                        ? 'Preparando micrófono...'
                        : 'Preparing microphone...'}
                    </p>
                  </>
                ) : !isRecording ? (
                  <>
                    <button
                      onClick={startRecording}
                      disabled={saving}
                      className="w-24 h-24 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-all hover:scale-105 active:scale-95"
                      data-testid="fab-start-recording-btn"
                    >
                      <Mic className="w-10 h-10" />
                    </button>
                    <p className="text-sm text-muted-foreground mt-4">
                      {locale === 'es-MX' 
                        ? 'Toca para grabar (máx 5 min)'
                        : 'Tap to record (max 5 min)'}
                    </p>
                  </>
                ) : (
                  <>
                    <button
                      onClick={stopRecording}
                      className="w-24 h-24 rounded-full bg-red-500 text-white flex items-center justify-center animate-pulse"
                      data-testid="fab-stop-recording-btn"
                    >
                      <Square className="w-10 h-10" />
                    </button>
                    <p className="text-2xl font-mono mt-4 text-red-500">
                      {formatTime(recordingTime)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {locale === 'es-MX' ? 'Grabando... Toca para detener' : 'Recording... Tap to stop'}
                    </p>
                  </>
                )}
                
                {saving && (
                  <div className="flex items-center gap-2 mt-4 text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>{locale === 'es-MX' ? 'Guardando...' : 'Saving...'}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {captureMode === 'text' && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleCloseModal}
                className="flex-1 rounded-full"
              >
                {t('ideas.cancel')}
              </Button>
              <Button
                onClick={handleQuickSave}
                disabled={saving || !body.trim()}
                className="flex-1 rounded-full bg-amber-500 hover:bg-amber-600"
                data-testid="quick-save-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                {t('ideas.save')}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
